// contexts/ModalContext.tsx
import React, { createContext, useContext, useState } from 'react';
import type { ProfileData } from '../types';
import { useAuth } from '../components/auth/hooks/useAuth';
import { useProfileManagement } from '../components/auth/useProfileManagement';

interface ModalContextType {
    showProfileModal: boolean;
    setShowProfileModal: (show: boolean) => void;
    showPasswordModal: boolean;
    setShowPasswordModal: (show: boolean) => void;
    error: string | null;
    setError: (error: string | null) => void;
    success: string | null;
    setSuccess: (success: string | null) => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    profileData: ProfileData;
    handleProfileUpdate: (e: React.FormEvent) => Promise<void>;
    handleDataChange: (data: Partial<ProfileData>) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [profileData, setProfileData] = useState<ProfileData>({
        fullName: user?.fullName || '',
        email: user?.email || ''
    });

    const profileManagement = useProfileManagement(
        undefined,
        setError,
        setSuccess,
        setIsLoading,
        setShowProfileModal,
        setShowPasswordModal
    );

    const handleDataChange = (data: Partial<ProfileData>) => {
        if ('fullName' in data || 'email' in data) {
            setProfileData(prev => ({ ...prev, ...data }));
        }
    };

    const value = {
        showProfileModal,
        setShowProfileModal,
        showPasswordModal,
        setShowPasswordModal,
        error,
        setError,
        success,
        setSuccess,
        isLoading,
        setIsLoading,
        profileData,
        handleProfileUpdate: (e: React.FormEvent) => profileManagement.handleProfileUpdate(e, profileData),
        handleDataChange
    };

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>
    );
};

export const useModalContext = () => {
    const context = useContext(ModalContext);
    if (context === undefined) {
        throw new Error('useModalContext must be used within a ModalProvider');
    }
    return context;
};